import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import {
  Sparkles, ArrowUpRight, ListChecks, AlertTriangle, Lightbulb,
  Zap, Clock, Layers, Loader2, UserCircle,
} from "lucide-react";

const KIND_META = {
  action: {
    icon: ListChecks,
    label: "Action",
    color: "#0e7490",
    bg: "bg-[#ecfeff]",
    border: "border-[#67e8f9]",
    chipBg: "bg-[#cffafe]",
    chipText: "text-[#155e75]",
  },
  concern: {
    icon: AlertTriangle,
    label: "Concern",
    color: "#c53030",
    bg: "bg-[#fff1f2]",
    border: "border-[#fda4af]",
    chipBg: "bg-[#fee2e2]",
    chipText: "text-[#991b1b]",
  },
  opportunity: {
    icon: Lightbulb,
    label: "Opportunity",
    color: "#b45309",
    bg: "bg-[#fffbeb]",
    border: "border-[#fcd34d]",
    chipBg: "bg-[#fef3c7]",
    chipText: "text-[#92400e]",
  },
};

const PRIORITY_STYLE = {
  high: "bg-[#fee2e2] text-[#991b1b]",
  medium: "bg-[#fef3c7] text-[#92400e]",
  low: "bg-[#e0f2fe] text-[#075985]",
};

const EFFORT_META = {
  quick: { label: "Quick win", icon: Zap, text: "text-[#166534]" },
  medium: { label: "Medium effort", icon: Clock, text: "text-[#92400e]" },
  deep: { label: "Deep work", icon: Layers, text: "text-[#3730a3]" },
};

function formatPeriod(period) {
  if (!period) return "";
  const [y, m] = period.split("-");
  const d = new Date(parseInt(y, 10), parseInt(m, 10) - 1, 1);
  return d.toLocaleString(undefined, { month: "short", year: "numeric" });
}

function ClientInitials({ name, logoUrl, size = "md" }) {
  const dims = size === "sm" ? "h-8 w-8 text-[10px]" : "h-10 w-10 text-xs";
  if (logoUrl) {
    return (
      <img
        src={logoUrl}
        alt={name}
        className={`${dims} rounded-xl object-contain bg-white border border-[#edf2f7] p-0.5 shrink-0`}
        onError={(e) => { e.currentTarget.style.display = "none"; }}
      />
    );
  }
  return (
    <div className={`${dims} rounded-xl bg-brand text-white flex items-center justify-center font-bold shrink-0`}>
      {(name || "??").slice(0, 2).toUpperCase()}
    </div>
  );
}

function HighlightRow({ highlight }) {
  const meta = KIND_META[highlight.kind] || KIND_META.action;
  const Icon = meta.icon;
  const priority = highlight.kind === "action" ? (highlight.priority || "medium") : null;
  const effort = highlight.kind === "action" ? (highlight.effort || "medium") : null;
  const effortMeta = effort ? EFFORT_META[effort] : null;
  const EffortIcon = effortMeta?.icon;

  return (
    <div
      className="rounded-xl border border-[#edf2f7] bg-white p-3 hover:border-[#cbd5e0] transition-colors"
      data-testid={`feed-highlight-${highlight.kind}`}
    >
      <div className="flex items-start gap-2.5">
        <div className={`h-6 w-6 rounded-md ${meta.bg} ${meta.border} border flex items-center justify-center shrink-0 mt-0.5`}>
          <Icon className="h-3 w-3" style={{ color: meta.color }} strokeWidth={2.4} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-2 flex-wrap">
            <div className="text-sm font-semibold text-[#1a202c] leading-snug">{highlight.title}</div>
            {priority && (
              <div className="flex items-center gap-1 shrink-0">
                <span
                  className={`inline-flex items-center px-1.5 py-0.5 rounded text-[9px] uppercase tracking-widest font-bold ${PRIORITY_STYLE[priority] || PRIORITY_STYLE.medium}`}
                >
                  {priority}
                </span>
                {effortMeta && (
                  <span className={`inline-flex items-center gap-1 text-[10px] font-semibold ${effortMeta.text}`}>
                    <EffortIcon className="h-3 w-3" strokeWidth={2.4} />
                  </span>
                )}
              </div>
            )}
            {highlight.metric && !priority && (
              <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-mono-tight font-semibold ${meta.chipBg} ${meta.chipText}`}>
                {highlight.metric}
              </span>
            )}
          </div>
          {highlight.detail && (
            <div className="text-[12px] text-[#4a5568] mt-1 leading-relaxed line-clamp-2">{highlight.detail}</div>
          )}
        </div>
      </div>
    </div>
  );
}

function ClientCard({ item, onOpen }) {
  return (
    <div
      className="card-soft p-4 flex flex-col gap-3 hover:shadow-[0_8px_20px_-4px_rgba(45,55,72,0.1)] transition-all"
      data-testid={`feed-client-${item.client_id}`}
    >
      <button
        type="button"
        onClick={onOpen}
        className="flex items-start justify-between gap-3 text-left group"
        data-testid={`feed-client-${item.client_id}-open`}
      >
        <div className="flex items-center gap-3 min-w-0">
          <ClientInitials name={item.client_name} logoUrl={item.client_logo_url} />
          <div className="min-w-0">
            <div className="text-sm font-bold text-[#1a202c] truncate group-hover:text-[#2c7a7b] transition-colors">
              {item.client_name}
            </div>
            <div className="text-[11px] text-[#a0aec0] font-mono-tight truncate">
              {item.client_domain} · {formatPeriod(item.period)}
            </div>
          </div>
        </div>
        <span className="inline-flex items-center gap-1 text-[10px] font-bold text-[#2c7a7b] shrink-0 mt-1 group-hover:gap-1.5 transition-all">
          OPEN <ArrowUpRight className="h-3 w-3" strokeWidth={2.6} />
        </span>
      </button>
      <div className="space-y-2">
        {item.highlights.map((h, i) => (
          <HighlightRow key={i} highlight={h} />
        ))}
      </div>
    </div>
  );
}

export default function AIInsightsFeed() {
  const navigate = useNavigate();
  const [items, setItems] = useState(null);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Hold the clients list alongside the feed so we can filter items down to
  // only ones the logged-in user owns (SEO Exec or Account Manager).
  const [clientsById, setClientsById] = useState({});
  const { user } = useAuth();
  // UI state — "mine" default, with a toggle to switch to "all" when the
  // user wants the agency-wide view. Admins with no assigned clients get
  // auto-flipped to "all" below so they don't see a confusing empty state.
  const [scope, setScope] = useState("mine");

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        // Fire both calls in parallel — the clients list is tiny and used
        // only for the user-id lookup; the feed is the expensive one.
        const [{ data }, { data: clientsList }] = await Promise.all([
          api.get("/ai-insights/feed?per_client=2"),
          api.get("/clients"),
        ]);
        if (!alive) return;
        setItems(data.items || []);
        setMeta({
          current_period: data.current_period,
          client_count: data.client_count,
          covered_count: data.covered_count,
        });
        const map = {};
        for (const c of clientsList) map[c.id] = c;
        setClientsById(map);
      } catch (e) {
        if (alive) setError(e?.response?.data?.detail || "Couldn't load AI insights feed");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  // Items I own = feed items whose client has me as either SEO Exec or AM.
  // Computed every render but it's O(N) with N ≤ ~50 clients — negligible.
  const myItems = useMemo(() => {
    if (!items || !user?.id) return [];
    return items.filter((it) => {
      const c = clientsById[it.client_id];
      if (!c) return false;
      return c.assigned_user_id === user.id || c.account_manager_user_id === user.id;
    });
  }, [items, clientsById, user?.id]);

  // Auto-flip to "all" when the user owns nothing — avoids an empty wall
  // for admins who aren't personally assigned to any accounts.
  useEffect(() => {
    if (!loading && items && items.length > 0 && myItems.length === 0 && scope === "mine") {
      setScope("all");
    }
  }, [loading, items, myItems.length, scope]);

  const shownItems = scope === "mine" ? myItems : items;

  if (loading) {
    return (
      <div className="card-soft p-8 flex items-center justify-center gap-3 text-xs text-[#a0aec0]" data-testid="ai-feed-loading">
        <Loader2 className="h-4 w-4 animate-spin text-[#4fd1c5]" />
        <span className="font-mono-tight">Loading AI insights…</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="card-soft p-6" data-testid="ai-feed-error">
        <div className="text-sm font-semibold text-[#c53030]">Couldn&apos;t load AI insights</div>
        <div className="text-[12px] text-[#4a5568] mt-1 font-mono-tight">{error}</div>
      </div>
    );
  }

  // Empty state — no client has a cached insight yet
  if (!items || items.length === 0) {
    return (
      <div
        className="card-flat p-8 relative overflow-hidden flex items-center gap-5"
        data-testid="ai-feed-empty"
      >
        <div className="h-14 w-14 rounded-2xl bg-gradient-to-br from-[#4fd1c5] to-[#319795] text-white flex items-center justify-center shrink-0 shadow-[0_8px_20px_-4px_rgba(56,178,172,0.4)]">
          <Sparkles className="h-6 w-6" strokeWidth={2} />
        </div>
        <div className="flex-1">
          <div className="text-[11px] uppercase tracking-widest text-[#a0aec0] font-semibold">AI Insights</div>
          <h2 className="font-display text-xl mt-1 text-[#1a202c]">No briefings yet</h2>
          <p className="text-sm text-[#4a5568] mt-1.5 leading-relaxed max-w-2xl">
            Insights auto-generate on the 1st of every month, or open any client and click <span className="font-semibold">Generate now</span> from the AI Insights tab to get your first briefing.
          </p>
          <button
            onClick={() => navigate("/clients")}
            data-testid="ai-feed-open-clients"
            className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-[#2c7a7b] hover:gap-2 transition-all"
          >
            Open clients <ArrowUpRight className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
    );
  }

  // Count uncovered clients to nudge user to generate the rest
  const uncovered = (meta?.client_count || 0) - (meta?.covered_count || 0);
  const isMine = scope === "mine";
  const title = isMine ? "What I need to action this month" : "What to action this month";
  const subtitle = isMine
    ? `Top 2 priorities for the ${myItems.length} client${myItems.length === 1 ? "" : "s"} assigned to you. Click any card to open the full briefing.`
    : "Top 2 priorities per client, drawn from Claude's monthly briefings. Click any client card to open the full briefing.";

  return (
    <div data-testid="ai-feed-root">
      <div className="flex items-end justify-between gap-3 flex-wrap mb-4">
        <div className="flex items-start gap-3">
          <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-[#4fd1c5] to-[#319795] text-white flex items-center justify-center shrink-0 shadow-[0_4px_12px_-2px_rgba(56,178,172,0.4)]">
            <Sparkles className="h-5 w-5" strokeWidth={2} />
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-widest text-[#a0aec0] font-semibold">
              AI Insights · {formatPeriod(meta?.current_period)}
            </div>
            <h2 className="font-display text-2xl mt-0.5 text-[#1a202c]" data-testid="ai-feed-title">{title}</h2>
            <p className="text-xs text-[#718096] mt-1">
              {subtitle}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {/* Scope toggle — mine vs all. Shown only when the user has at
              least one assigned client; otherwise "mine" is useless and we
              default to "all" anyway (see auto-flip effect above). */}
          {myItems.length > 0 && (
            <div
              className="inline-flex items-center gap-0.5 p-0.5 rounded-xl border border-[#e2e8f0] bg-white"
              data-testid="ai-feed-scope-toggle"
            >
              <button
                type="button"
                onClick={() => setScope("mine")}
                data-testid="ai-feed-scope-mine"
                className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[11px] font-semibold transition-colors ${
                  isMine ? "bg-[#4fd1c5]/15 text-[#2c7a7b]" : "text-[#718096] hover:text-[#4a5568]"
                }`}
              >
                <UserCircle className="h-3 w-3" strokeWidth={2.2} />
                Mine ({myItems.length})
              </button>
              <button
                type="button"
                onClick={() => setScope("all")}
                data-testid="ai-feed-scope-all"
                className={`inline-flex items-center px-2.5 py-1.5 rounded-lg text-[11px] font-semibold transition-colors ${
                  !isMine ? "bg-[#4fd1c5]/15 text-[#2c7a7b]" : "text-[#718096] hover:text-[#4a5568]"
                }`}
              >
                All ({items.length})
              </button>
            </div>
          )}
          <div className="text-[11px] text-[#a0aec0] font-mono-tight text-right">
            <div>
              <span className="text-[#1a202c] font-bold">{meta?.covered_count}</span> of {meta?.client_count} clients with briefings
            </div>
            {uncovered > 0 && (
              <button
                onClick={() => navigate("/clients")}
                className="mt-1 text-[#2c7a7b] hover:underline"
                data-testid="ai-feed-uncovered"
              >
                Generate the remaining {uncovered} →
              </button>
            )}
          </div>
        </div>
      </div>
      {/* Empty "mine" state — happens when a logged-in user has no clients
          assigned and the auto-flip effect hasn't fired yet, or when they
          manually toggle "Mine" after being on "All". */}
      {isMine && myItems.length === 0 ? (
        <div className="card-flat p-8 text-center" data-testid="ai-feed-mine-empty">
          <div className="text-sm text-[#4a5568]">You&apos;re not assigned to any clients yet.</div>
          <button
            type="button"
            onClick={() => setScope("all")}
            className="mt-3 text-xs font-semibold text-[#2c7a7b] hover:underline"
          >
            Show all clients instead →
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          {shownItems.map((item) => (
            <ClientCard
              key={item.client_id}
              item={item}
              onOpen={() => navigate(`/clients/${item.client_id}/ai-insights`)}
            />
          ))}
        </div>
      )}
    </div>
  );
}
